import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { FiUpload, FiSearch, FiX } from 'react-icons/fi';
import { markOnboardingStep } from '../utils/onboarding';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/common/Button';
import PageHeader from '../components/common/PageHeader';
import Chip from '../components/common/Chip';
import ResolutionModal from '../components/drafts/ResolutionModal';
import NavGlyph from '../components/common/NavGlyph';
import Icon from '../components/common/Icon';
import { RESOLUTION_SECTIONS } from '../data/resolutions';
import { aiAPI } from '../services/api';
import { useUI } from '../context/UIContext';
import { useApp } from '../context/AppContext';

function getSectionTheme(sectionId) {
  const map = {
    directors_kmp: {
      bg: '#edeef3',
      color: '#3a4a66',
      borderColor: 'rgba(58, 74, 102, 0.15)',
    },
    share_capital: {
      bg: '#fbf5e6',
      color: '#8a6a1f',
      borderColor: 'rgba(138, 106, 31, 0.15)',
    },
    banking_finance: {
      bg: '#e4f5f3',
      color: '#145f5f',
      borderColor: 'rgba(20, 95, 95, 0.15)',
    },
    restructuring: {
      bg: '#fbf0ed',
      color: '#9a564a',
      borderColor: 'rgba(154, 86, 74, 0.15)',
    },
    affidavits_bonds: {
      bg: '#f5f0fa',
      color: '#6a1b9a',
      borderColor: 'rgba(106, 27, 154, 0.15)',
    },
    accounts_audit_dividend: {
      bg: '#edf7ed',
      color: '#2e7d32',
      borderColor: 'rgba(46, 125, 50, 0.15)',
    },
    meetings_governance: {
      bg: '#edf6fc',
      color: '#1565c0',
      borderColor: 'rgba(21, 101, 192, 0.15)',
    },
  };
  return map[sectionId] || { bg: '#faf7f2', color: '#6b6354', borderColor: 'rgba(226,217,200,0.65)' };
}

function ResolutionCard({ item, sectionId, onOpen }) {
  const theme = getSectionTheme(sectionId);
  return (
    <div
      className="cs-resolution-card group rounded-[14px] px-4 py-3.5 cursor-pointer"
      onClick={() => onOpen(item)}
    >
      <div className="flex items-start gap-3">
        <span
          className="shrink-0 grid place-items-center w-9 h-9 rounded-[11px] border transition-all duration-300 group-hover:scale-105"
          style={{ background: theme.bg, color: theme.color, borderColor: theme.borderColor }}
        >
          <NavGlyph icon={item.icon} size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div
            className="text-[13px] font-bold leading-[1.3] text-[#08111f] mb-0.5 transition-colors group-hover:text-[#8a6a1f]"
            style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.01em' }}
          >
            {item.title}
          </div>
          <div
            className="text-[10.5px] leading-[1.4] text-[#7a7060] font-medium"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {item.subtitle}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ResolutionsPage() {
  const { showToast } = useUI();
  const { activeCompany } = useApp();
  const location = useLocation();
  const [selected, setSelected] = useState(null);
  const [referenceDoc, setReferenceDoc] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('all');

  // Getting-started runway: reaching any drafting surface completes the step.
  useEffect(() => { markOnboardingStep('visited-drafting'); }, []);

  // Deep-link: open a specific resolution from spotlight search
  useEffect(() => {
    const resId = location.state?.resolutionId;
    if (!resId) return;
    const found = RESOLUTION_SECTIONS
      .flatMap((s) => s.items)
      .find((item) => item.id === resId);
    if (found) setSelected(found);
    // Clear state so refreshing doesn't re-open
    window.history.replaceState({}, '');
  }, [location.state?.resolutionId]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const ext = String(file.name).toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!['.txt', '.docx'].includes(ext)) {
      showToast('Only .txt and .docx files are supported', 'error');
      event.target.value = '';
      return;
    }

    setUploadBusy(true);
    showToast(`Parsing ${file.name}\u2026`, 'info');
    try {
      const res = await aiAPI.parseReferenceDocument(file);
      if (res?.success && res.data?.reference) {
        setReferenceDoc(res.data.reference);
        showToast('Uploaded \u2014 content will be appended to your next AI draft', 'success');
      } else {
        throw new Error(res?.message || 'Failed to parse reference document');
      }
    } catch (err) {
      showToast(err.message || 'Failed to parse reference document', 'error');
    } finally {
      setUploadBusy(false);
      if (event.target) event.target.value = '';
    }
  };

  const removeReferenceDoc = () => setReferenceDoc(null);

  // Dynamic Filtering Logic
  const filteredSections = useMemo(() => {
    return RESOLUTION_SECTIONS.map(section => {
      // Filter by active section tab
      if (activeSection !== 'all' && section.id !== activeSection) {
        return null;
      }

      // Filter items by search query
      const filteredItems = section.items.filter(item => 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.subtitle.toLowerCase().includes(searchQuery.toLowerCase())
      );

      if (filteredItems.length === 0) return null;

      return {
        ...section,
        items: filteredItems
      };
    }).filter(Boolean);
  }, [searchQuery, activeSection]);

  // Line icons per category (emoji chrome retired 2026-07-07 — use <Icon />).
  const SECTION_ICONS = {
    all: 'portfolio',
    directors_kmp: 'directors',
    share_capital: 'capital',
    banking_finance: 'money',
    restructuring: 'company',
    affidavits_bonds: 'legal',
    accounts_audit_dividend: 'audit',
    meetings_governance: 'governance',
  };

  const categories = useMemo(() => {
    return [
      { id: 'all', label: 'All Templates' },
      ...RESOLUTION_SECTIONS.map(s => ({
        id: s.id,
        label: s.title
      }))
    ];
  }, []);

  const hasResults = filteredSections.length > 0;

  return (
    <AppLayout pageTitle="Resolutions">
      <PageHeader
        title={(
          <span className="inline-flex items-center align-middle">
            Resolution Drafting Engine
            <span
              className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[1px] px-[7px] py-[2px] rounded-[5px] ml-[7px] align-middle"
              style={{ background: 'linear-gradient(135deg, #b8973a, #d4820a)', color: '#08111f' }}
            >
              <Icon name="✨" size={10} />AI
            </span>
          </span>
        )}
        subtitle="60+ resolution types · Click any to AI-draft with professional letterhead · All documents fully editable & downloadable"
      />

      <div className="cs-upload-dropzone flex items-center gap-5 rounded-2xl p-5 mb-6 flex-wrap sm:flex-nowrap">
        <div
          className="shrink-0 grid place-items-center w-12 h-12 rounded-xl"
          style={{ background: '#fbf5e6', color: '#8a6a1f' }}
        >
          <FiUpload size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[15px] text-[#08111f]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Upload Reference Draft Resolution
          </div>
          <div className="text-[12px] text-[#5a5145] mt-[3px] leading-relaxed">
            Upload a .txt or .docx file. Our AI will analyze it and incorporate its agenda, clauses, and structure into your next AI resolution draft.
          </div>
          {referenceDoc && (
            <div className="text-[11px] text-[#4a6b3f] mt-2 flex items-center gap-2 font-medium flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#e8efe5]">
                ✓ {referenceDoc.filename}
              </span>
              {referenceDoc.agendaHeader && <span style={{ color: '#7a7060' }}>— {referenceDoc.agendaHeader}</span>}
              <button
                className="underline text-[10px] text-[#9a564a] hover:text-[#7d3b30] font-semibold"
                onClick={removeReferenceDoc}
              >
                remove
              </button>
            </div>
          )}
        </div>
        <Button
          as="label"
          variant="gold"
          size="sm"
          loading={uploadBusy}
          leftIcon={<FiUpload size={15} />}
          className="flex-shrink-0 cursor-pointer !h-[36px] !rounded-xl"
        >
          {uploadBusy ? 'Parsing…' : 'Upload & Attach'}
          <input type="file" accept=".txt,.docx" className="hidden" onChange={handleUpload} disabled={uploadBusy} />
        </Button>
      </div>

      {/* Embedded Quick Search Row */}
      <div className="relative mb-4">
        <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7a7060]" size={16} />
        <input
          type="text"
          placeholder="Search through 60+ resolution templates by name, regulation, or form..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-[38px] pr-9 py-2.5 rounded-xl border-[1.5px] text-[13px] outline-none transition-all focus:border-[#145f5f] focus:shadow-sm"
          style={{ borderColor: '#ddd6c6', background: '#ffffff', fontFamily: "'Outfit', sans-serif" }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#7a7060] hover:text-[#08111f]"
          >
            <FiX size={15} />
          </button>
        )}
      </div>

      {/* Category Tabs Pill Selector */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 cs-premium-scrollbar">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveSection(cat.id)}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-[12px] font-bold transition-all whitespace-nowrap border ${
              activeSection === cat.id
                ? 'bg-[#145f5f] border-[#145f5f] text-white shadow-sm'
                : 'bg-white border-[#e2d9c8] text-[#5a5145] hover:bg-[#faf9f6]'
            }`}
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {SECTION_ICONS[cat.id] && <Icon name={SECTION_ICONS[cat.id]} size={13} />}
            {cat.label}
          </button>
        ))}
      </div>

      {/* Resolutions Grid list */}
      <div className="space-y-7">
        {filteredSections.map((section) => (
          <section key={section.id} className="animate-in fade-in duration-300">
            <div
              className="flex items-center gap-2.5 mb-[12px] pb-[8px] border-b-[1.5px]"
              style={{
                borderColor: '#e2d9c8',
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '17px',
                fontWeight: 700,
                color: '#08111f'
              }}
            >
              <span className="w-[3px] h-[13px] rounded-full bg-[#145f5f]" />
              <span>{section.title.replace(/[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF]/g, '').trim()}</span>
              <Chip bg="#145f5f" color="#fff">{section.items.length}</Chip>
            </div>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
              {section.items.map((item) => (
                <ResolutionCard key={item.id} item={item} sectionId={section.id} onOpen={setSelected} />
              ))}
            </div>
          </section>
        ))}

        {/* Empty Search Result State */}
        {!hasResults && (
          <div className="text-center py-16 bg-white rounded-2xl border" style={{ borderColor: '#e2d9c8' }}>
            <div className="text-[28px] mb-2">🔍</div>
            <h3 className="text-[14px] font-bold text-[#08111f]">No templates found</h3>
            <p className="text-[12px] text-[#7a7060] mt-1">
              We couldn't find any resolutions matching "{searchQuery}". Try adjusting your keywords.
            </p>
            <button
              onClick={() => { setSearchQuery(''); setActiveSection('all'); }}
              className="mt-4 px-4 py-2 bg-[#fbf5e6] text-[#8a6a1f] border border-[#8a6a1f]/20 rounded-xl text-[12px] font-semibold hover:bg-[#faf4e2]"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      <ResolutionModal
        item={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        referenceDoc={referenceDoc}
        companyCin={activeCompany?.cin}
        companyName={activeCompany?.name}
      />
    </AppLayout>
  );
}
